import { useEffect, useRef, useState } from "react";

interface Props {
  giftNumber: string;
}

interface Point {
  x: number;
  y: number;
}

export function ScratchReveal({ giftNumber }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scratchingRef = useRef(false);
  const lastPointRef = useRef<Point | null>(null);
  const strokesRef = useRef(0);
  const [complete, setComplete] = useState(false);
  const [canvasVisible, setCanvasVisible] = useState(true);
  const [scratching, setScratching] = useState(false);

  useEffect(() => {
    setComplete(false);
    setCanvasVisible(true);
    setScratching(false);
    strokesRef.current = 0;
    lastPointRef.current = null;
  }, [giftNumber]);

  useEffect(() => {
    if (!complete) return;
    const timer = window.setTimeout(() => setCanvasVisible(false), 520);
    return () => window.clearTimeout(timer);
  }, [complete]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || complete) return;

    function paintFoil() {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(rect.width * ratio);
      canvas.height = Math.round(rect.height * ratio);
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) return;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);

      const gradient = context.createLinearGradient(0, 0, rect.width, rect.height);
      gradient.addColorStop(0, "#8f8790");
      gradient.addColorStop(.22, "#d9d4d7");
      gradient.addColorStop(.48, "#9e969f");
      gradient.addColorStop(.72, "#eee9ea");
      gradient.addColorStop(1, "#8b828b");
      context.fillStyle = gradient;
      context.fillRect(0, 0, rect.width, rect.height);

      for (let index = 0; index < 90; index += 1) {
        const x = Math.random() * rect.width;
        const y = Math.random() * rect.height;
        const size = .4 + Math.random() * 1.4;
        context.fillStyle = index % 2 === 0 ? "rgba(255,255,255,.32)" : "rgba(64,53,63,.16)";
        context.beginPath();
        context.arc(x, y, size, 0, Math.PI * 2);
        context.fill();
      }

      context.fillStyle = "rgba(58, 45, 54, .86)";
      context.font = "700 15px Heebo";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.direction = "rtl";
      context.fillText("גרדי בעדינות כדי לגלות", rect.width / 2, rect.height / 2);
      context.setTransform(1, 0, 0, 1, 0, 0);
    }

    paintFoil();
    const observer = new ResizeObserver(paintFoil);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [giftNumber, complete]);

  function finishReveal() {
    if (complete) return;
    setComplete(true);
    setScratching(false);
    navigator.vibrate?.([18, 28, 22]);
  }

  function clearedRatio(canvas: HTMLCanvasElement) {
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return 0;
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const sampleStep = 12;
    let cleared = 0;
    let sampled = 0;
    for (let y = 0; y < canvas.height; y += sampleStep) {
      for (let x = 0; x < canvas.width; x += sampleStep) {
        sampled += 1;
        if (pixels[(y * canvas.width + x) * 4 + 3] < 80) cleared += 1;
      }
    }
    return sampled ? cleared / sampled : 0;
  }

  function scratch(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!scratchingRef.current || complete) return;
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d", { willReadFrequently: true });
    if (!canvas || !context) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = canvas.width / rect.width;
    const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const previous = lastPointRef.current ?? point;

    context.save();
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.globalCompositeOperation = "destination-out";
    context.lineWidth = 34;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.beginPath();
    context.moveTo(previous.x, previous.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    context.beginPath();
    context.arc(point.x, point.y, 17, 0, Math.PI * 2);
    context.fill();
    context.restore();

    lastPointRef.current = point;
    strokesRef.current += 1;
    event.currentTarget.parentElement?.style.setProperty("--scratch-x", `${point.x}px`);
    event.currentTarget.parentElement?.style.setProperty("--scratch-y", `${point.y}px`);

    if (strokesRef.current % 9 === 0) navigator.vibrate?.(5);
    if (strokesRef.current % 6 === 0 && clearedRatio(canvas) >= .42) finishReveal();
  }

  function stopScratching() {
    scratchingRef.current = false;
    lastPointRef.current = null;
    setScratching(false);
  }

  return (
    <div
      className={`gift-line scratch-reveal${scratching ? " is-scratching" : ""}${complete ? " is-complete" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={complete ? `המתנה נמצאת בקופסה מספר ${giftNumber}` : "גרדי את שכבת הכסף כדי לגלות את מספר המתנה"}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          finishReveal();
        }
      }}
    >
      <span>המתנה שלך היום מחכה במספר <strong>{giftNumber}</strong></span>
      {canvasVisible && (
        <div className="scratch-foil" aria-hidden="true">
          <canvas
            ref={canvasRef}
            className="scratch-canvas"
            onPointerDown={(event) => {
              scratchingRef.current = true;
              setScratching(true);
              try {
                event.currentTarget.setPointerCapture(event.pointerId);
              } catch {
                // Some assistive and synthetic pointer events do not create a capturable pointer.
              }
              scratch(event);
            }}
            onPointerMove={scratch}
            onPointerUp={stopScratching}
            onPointerCancel={stopScratching}
          />
          <i className="scratch-dust" />
        </div>
      )}
      <span className="sr-only" role="status">{complete ? `המתנה נמצאת בקופסה מספר ${giftNumber}` : ""}</span>
    </div>
  );
}
